import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LookupCategory,
  LookupOption,
  LookupOptionResponse,
  LookupOptionsListResponse,
} from '@stfors/shared-types';
import { api } from '../client';

// Reads are cached for 5 min — these change rarely (admin-managed) but
// we still want the admin's edits to be visible quickly. invalidate
// queries on mutations to refetch.
const STALE_MS = 5 * 60 * 1000;

export function useLookups(
  category?: LookupCategory,
  opts?: { enabled?: boolean },
) {
  const qs = category !== undefined ? `?category=${category}` : '';
  return useQuery({
    queryKey: ['lookups', { category: category ?? null }],
    queryFn: ({ signal }) =>
      api.get<LookupOptionsListResponse>(`/api/lookups${qs}`, signal),
    staleTime: STALE_MS,
    // `enabled` lets large categories (us_state/street_type) defer their fetch
    // until an admin expands the card. Defaults to eager.
    enabled: opts?.enabled ?? true,
  });
}

export interface CreateLookupInput {
  category: LookupCategory;
  value: string;
  label?: string;
  sort_order?: number;
}

export function useCreateLookup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLookupInput) =>
      api.post<LookupOptionResponse>('/api/lookups', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

export interface UpdateLookupInput {
  id: number;
  value?: string;
  label?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

export function useUpdateLookup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateLookupInput) =>
      api.patch<LookupOptionResponse>(`/api/lookups/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

export function useDeleteLookup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/lookups/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

// Convenience: filter a hook result down to the active options for one
// category, sorted by sort_order then value. Used by <LookupSelect> and forms.
export function activeOptionsFor(
  options: LookupOption[] | undefined,
  category: LookupCategory,
): LookupOption[] {
  if (!options) return [];
  return options
    .filter((o) => o.category === category && o.is_active)
    .sort(
      (a, b) =>
        a.sort_order - b.sort_order || a.value.localeCompare(b.value),
    );
}

// Resolve a stored code to its display label for read-only surfaces (status
// pills, table cells). Searches ALL options (incl. inactive/legacy) so a
// historical value still renders; falls back to the value itself when unknown.
export function labelForValue(
  options: LookupOption[] | undefined,
  category: LookupCategory,
  value: string | null | undefined,
): string {
  if (!value) return '';
  const match = options?.find((o) => o.category === category && o.value === value);
  return match?.label ?? match?.value ?? value;
}

// Ergonomic hook for read-only displays: returns a labeler bound to one
// category. e.g. `const statusLabel = useLookupLabel('project_status');`
export function useLookupLabel(
  category: LookupCategory,
): (value: string | null | undefined) => string {
  const { data } = useLookups(category);
  return (value) => labelForValue(data?.lookup_options, category, value);
}
